/// <amd-module name="Core/Medius.Core.Web/Scripts/components/emailStatusTab/components/emailEntry"/>

import { FlexLayout, Text } from '@medius/ui-controls';
import { translate } from 'Core/Medius.Core.Web/Scripts/lib/globalization';
import * as React from 'react';
import { EmailEntryDto, EmailType, parseEmailType } from '../models/emailEntryDto';
import { EmailStatusRenderer, EmailStatusRendererRecipient } from '../common/emailStatusRenderer';
import { getUserTimeProvider } from 'Core/Medius.Core.Web/Scripts/lib/date/date';
import { DatePresenter } from 'Core/Medius.Core.Web/Scripts/shared/components/datePresenter';

type EmailEntryProps = {
    emailEntry: EmailEntryDto;
};

export const EmailEntry = ({ emailEntry }: EmailEntryProps): React.ReactElement => {
    const rendererRecipients: EmailStatusRendererRecipient[] = emailEntry.recipients.map((recipient) => ({
        emailAddress: recipient.emailAddress,
        type: recipient.type,
        status: recipient.emailStatus,
        latestEventReason: recipient.latestEventReason,
    }));
    const timeProvider = getUserTimeProvider();
    const date = new Date(emailEntry.timestamp);
    
    const getEmailTitle = (): string => {
        switch (parseEmailType(emailEntry.emailType)) {
            case EmailType.SendInvoiceRejectionToSupplier:
                return translate('#Core/invoiceRejectionEmailSent');
            case EmailType.SendToSupplier:
                return translate('#Core/purchaseOrderEmailSentToSupplier');
            case EmailType.SendInvoiceImage:
                return translate('#Core/emailWithInvoiceImage');
            case EmailType.SendCreditMemoToSupplier:
                return translate('#Core/emailWithShortPayment');
            case EmailType.SendAdjustmentRequestToSupplier:
                return translate('#Core/emailWithAdjustmentRequest');
            default:
                return translate('#Core/emailSent');
        }
    };
    
    return (
        <FlexLayout
            direction="column"
            itemRowSpacing="small"
            className="email-entry"
        >
            <Text variant="heading3" className="email-entry__title" data-testid="email-title">
                {getEmailTitle()}
            </Text>

            <FlexLayout itemColumnSpacing="small">
                <Text variant="body" id="email-entry__sender" data-testid="sender-name">
                    {emailEntry.senderName}
                </Text>
                <Text variant="body" id="email-entry__timestamp" data-testid="timestamp">
                    • <DatePresenter 
                        date={timeProvider.toUserTimeZoneFromBrowserDate(date)} 
                        format="g" 
                    />
                </Text>
            </FlexLayout>

            <EmailStatusRenderer
                recipients={rendererRecipients}
                layout="flex"
                labels={{
                    to: translate('#Core/emailTo'),
                    cc: translate('#Core/emailCc'),
                    bcc: translate('#Core/emailBcc'),
                }}
            />
        </FlexLayout>
    );
};
